import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Settings, Brain, Volume2, CheckCircle, AlertCircle, ChevronRight
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserCoach } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { fetchCoachConfig, saveCoachConfig } from '../../lib/ai-coach';
import { fetchGhlSettings, saveGhlSettings } from '../../lib/ghl';
import { fetchHubSpotSettings, saveHubSpotSettings } from '../../lib/hubspot';

export function AICoachSettings() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [elevenlabsApiKey, setElevenlabsApiKey] = useState('');
  const [elevenlabsAgentId, setElevenlabsAgentId] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [showElevenlabsKey, setShowElevenlabsKey] = useState(false);
  const [hasElevenlabsKey, setHasElevenlabsKey] = useState(false);
  const [replaceElevenlabsKey, setReplaceElevenlabsKey] = useState(false);
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [cloneName, setCloneName] = useState('');

  const [ghlLocationId, setGhlLocationId] = useState('');
  const [ghlPipelineId, setGhlPipelineId] = useState('');
  const [ghlPipelineStageId, setGhlPipelineStageId] = useState('');
  const [ghlCustomToken, setGhlCustomToken] = useState('');
  const [showGhlToken, setShowGhlToken] = useState(false);
  const [hasGhlCustomToken, setHasGhlCustomToken] = useState(false);
  const [replaceGhlToken, setReplaceGhlToken] = useState(false);
  const [isSavingGhl, setIsSavingGhl] = useState(false);

  const [hubspotPortalId, setHubspotPortalId] = useState('');
  const [hubspotPipelineId, setHubspotPipelineId] = useState('');
  const [hubspotPipelineStageId, setHubspotPipelineStageId] = useState('');
  const [hubspotAccessToken, setHubspotAccessToken] = useState('');
  const [showHubspotToken, setShowHubspotToken] = useState(false);
  const [hasHubspotToken, setHasHubspotToken] = useState(false);
  const [replaceHubspotToken, setReplaceHubspotToken] = useState(false);
  const [isSavingHubspot, setIsSavingHubspot] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    const init = async () => {
      try {
        const coach = await isUserCoach(user.id);
        if (!coach) { navigate('/login'); return; }

        const { data: coachRecord } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coachRecord) { navigate('/login'); return; }
        setCoachId(coachRecord.id);

        const config = await fetchCoachConfig(coachRecord.id);
        if (config) {
          setElevenlabsAgentId(config.elevenlabsAgentId);
          setIsEnabled(config.isEnabled);
          setHasElevenlabsKey(config.hasElevenlabsApiKey);
          setSetupCompleted(config.setupCompleted);
          setCloneName(config.cloneName);
        }

        const ghl = await fetchGhlSettings(coachRecord.id);
        if (ghl) {
          setGhlLocationId(ghl.locationId);
          setGhlPipelineId(ghl.pipelineId);
          setGhlPipelineStageId(ghl.pipelineStageId);
          setHasGhlCustomToken(ghl.hasCustomToken);
        }

        const hs = await fetchHubSpotSettings(coachRecord.id);
        if (hs) {
          setHubspotPortalId(hs.portalId);
          setHubspotPipelineId(hs.pipelineId);
          setHubspotPipelineStageId(hs.pipelineStageId);
          setHasHubspotToken(hs.hasToken);
        }
      } catch (err) {
        console.error('Error loading AI Coach settings:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const handleSaveGhl = async () => {
    if (!coachId) return;
    setIsSavingGhl(true);
    setToast(null);

    try {
      await saveGhlSettings(coachId, {
        locationId: ghlLocationId,
        pipelineId: ghlPipelineId,
        pipelineStageId: ghlPipelineStageId,
        customToken: replaceGhlToken ? ghlCustomToken : undefined,
      });
      if (ghlCustomToken.trim().length > 0) {
        setHasGhlCustomToken(true);
        setGhlCustomToken('');
        setReplaceGhlToken(false);
      }
      setToast({ type: 'success', message: 'Go High Level settings saved' });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to save GHL settings' });
    } finally {
      setIsSavingGhl(false);
    }
  };

  const handleSaveHubspot = async () => {
    if (!coachId) return;
    setIsSavingHubspot(true);
    setToast(null);

    try {
      await saveHubSpotSettings(coachId, {
        portalId: hubspotPortalId,
        pipelineId: hubspotPipelineId,
        pipelineStageId: hubspotPipelineStageId,
        accessToken: replaceHubspotToken ? hubspotAccessToken : undefined,
      });
      if (hubspotAccessToken.trim().length > 0) {
        setHasHubspotToken(true);
        setHubspotAccessToken('');
        setReplaceHubspotToken(false);
      }
      setToast({ type: 'success', message: 'HubSpot settings saved' });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to save HubSpot settings' });
    } finally {
      setIsSavingHubspot(false);
    }
  };

  const handleSave = async () => {
    if (!coachId) return;
    setIsSaving(true);
    setToast(null);

    try {
      await saveCoachConfig(coachId, {
        openaiApiKey: '',
        openaiAssistantId: '',
        elevenlabsApiKey,
        elevenlabsAgentId,
        isEnabled,
      });
      if (elevenlabsApiKey.trim().length > 0) {
        setHasElevenlabsKey(true);
        setElevenlabsApiKey('');
        setReplaceElevenlabsKey(false);
      }
      setToast({ type: 'success', message: 'Settings saved successfully' });
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to save settings' });
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading AI Coach settings..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-sage-500 rounded-xl shadow-soft">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">AI Coach Settings</h1>
              <p className="freud-text-sm text-chocolate-500">Configure your AI coaching assistant</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 p-4 rounded-xl ${
              toast.type === 'success'
                ? 'bg-sage-100 text-sage-700'
                : 'bg-warm-100 text-warm-700'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm font-medium">{toast.message}</p>
          </motion.div>
        )}

        {/* AI Coach Clone Setup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sage-100 rounded-lg">
                <Brain className="w-5 h-5 text-sage-600" />
              </div>
              <div>
                <h2 className="freud-heading-base text-chocolate-900">
                  AI Coach Clone {cloneName ? `- ${cloneName}` : ''}
                </h2>
                <p className="freud-text-sm text-chocolate-500">
                  {setupCompleted
                    ? 'Your AI Coach Clone is configured. Edit setup to make changes.'
                    : 'Set up your AI Coach Clone with your style, methodology, and knowledge base.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/coach/ai-setup')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-sage-600 text-white rounded-xl text-sm font-semibold hover:bg-sage-700 transition-all"
            >
              {setupCompleted ? 'Edit Setup' : 'Start Setup'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {setupCompleted && (
            <div className="mt-4 flex items-center gap-2 text-sm text-sage-600">
              <CheckCircle className="w-4 h-4" />
              <span>Setup complete - AI Coach Clone is ready</span>
            </div>
          )}
        </motion.div>

        {/* ElevenLabs Voice Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-warm-100 rounded-lg">
              <Volume2 className="w-5 h-5 text-warm-600" />
            </div>
            <div>
              <h2 className="freud-heading-base text-chocolate-900">ElevenLabs Voice Configuration</h2>
              <p className="freud-text-sm text-chocolate-500">Optional: Enable voice-based AI coaching</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                ElevenLabs API Key
              </label>
              {hasElevenlabsKey && !replaceElevenlabsKey ? (
                <div className="flex items-center justify-between rounded-xl border border-sage-200 bg-sage-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-600" />
                    <span className="text-sm text-sage-700 font-medium">Key configured (encrypted)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplaceElevenlabsKey(true)}
                    className="text-xs font-medium text-sage-700 hover:text-sage-900 underline"
                  >
                    Replace
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <input
                      type={showElevenlabsKey ? 'text' : 'password'}
                      value={elevenlabsApiKey}
                      onChange={(e) => setElevenlabsApiKey(e.target.value)}
                      placeholder="Enter your ElevenLabs API key"
                      className="freud-input pr-20 font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowElevenlabsKey(!showElevenlabsKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-chocolate-400 hover:text-chocolate-600"
                    >
                      {showElevenlabsKey ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {hasElevenlabsKey && (
                    <button
                      type="button"
                      onClick={() => { setReplaceElevenlabsKey(false); setElevenlabsApiKey(''); }}
                      className="text-xs text-chocolate-500 hover:text-chocolate-700 mt-1"
                    >
                      Cancel replace
                    </button>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                ElevenLabs Agent ID
              </label>
              <input
                type="text"
                value={elevenlabsAgentId}
                onChange={(e) => setElevenlabsAgentId(e.target.value)}
                placeholder="Enter your ElevenLabs Agent ID"
                className="freud-input font-mono text-sm"
              />
            </div>
          </div>
        </motion.div>

        {/* Go High Level Integration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="freud-heading-base text-chocolate-900">Go High Level Integration</h2>
                {ghlLocationId.trim() ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-sage-700 bg-sage-100 px-2 py-0.5 rounded-full">
                    <CheckCircle className="w-3 h-3" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-chocolate-500 bg-cream-100 px-2 py-0.5 rounded-full">
                    Not configured
                  </span>
                )}
              </div>
              <p className="freud-text-sm text-chocolate-500">
                Auto-push new leads from assessments and lead magnet sign-ups into your GHL sub-account
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Location ID <span className="text-chocolate-400 font-normal">(required)</span>
              </label>
              <input
                type="text"
                value={ghlLocationId}
                onChange={(e) => setGhlLocationId(e.target.value)}
                placeholder="e.g. ve9EPM428h8vShlRW1KT"
                className="freud-input font-mono text-sm"
              />
              <p className="mt-1 text-xs text-chocolate-400">
                Found in your GHL sub-account URL or Settings &rarr; Business Info
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Pipeline ID
              </label>
              <input
                type="text"
                value={ghlPipelineId}
                onChange={(e) => setGhlPipelineId(e.target.value)}
                placeholder="e.g. abc123def456"
                className="freud-input font-mono text-sm"
              />
              <p className="mt-1 text-xs text-chocolate-400">
                GHL &rarr; Opportunities &rarr; Pipelines &rarr; select pipeline &rarr; copy ID from URL
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Pipeline Stage ID <span className="text-chocolate-400 font-normal">(for new leads)</span>
              </label>
              <input
                type="text"
                value={ghlPipelineStageId}
                onChange={(e) => setGhlPipelineStageId(e.target.value)}
                placeholder="e.g. stage_xyz789"
                className="freud-input font-mono text-sm"
              />
              <p className="mt-1 text-xs text-chocolate-400">
                The stage where incoming leads should land (e.g. "New Lead"). Get the ID from the GHL API or pipeline settings.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Custom GHL Token{' '}
                <span className="text-chocolate-400 font-normal">
                  (only if you have your own GHL account, not on our agency)
                </span>
              </label>
              {hasGhlCustomToken && !replaceGhlToken ? (
                <div className="flex items-center justify-between rounded-xl border border-sage-200 bg-sage-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-600" />
                    <span className="text-sm text-sage-700 font-medium">Token configured (encrypted)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplaceGhlToken(true)}
                    className="text-xs font-medium text-sage-700 hover:text-sage-900 underline"
                  >
                    Replace
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <input
                      type={showGhlToken ? 'text' : 'password'}
                      value={ghlCustomToken}
                      onChange={(e) => setGhlCustomToken(e.target.value)}
                      placeholder="Paste your GHL Private Integration token"
                      className="freud-input pr-20 font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGhlToken(!showGhlToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-chocolate-400 hover:text-chocolate-600"
                    >
                      {showGhlToken ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {hasGhlCustomToken && (
                    <button
                      type="button"
                      onClick={() => { setReplaceGhlToken(false); setGhlCustomToken(''); }}
                      className="text-xs text-chocolate-500 hover:text-chocolate-700 mt-1"
                    >
                      Cancel replace
                    </button>
                  )}
                </>
              )}
              <p className="mt-1 text-xs text-chocolate-400">
                GHL &rarr; Settings &rarr; Private Integrations &rarr; create token with Contacts + Opportunities scopes
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveGhl}
                disabled={isSavingGhl}
                className="freud-btn-primary px-6 py-2.5 disabled:opacity-60 text-sm"
              >
                {isSavingGhl ? 'Saving...' : 'Save GHL Settings'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* HubSpot Integration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Settings className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="freud-heading-base text-chocolate-900">HubSpot Integration</h2>
                {hasHubspotToken ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-sage-700 bg-sage-100 px-2 py-0.5 rounded-full">
                    <CheckCircle className="w-3 h-3" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-chocolate-500 bg-cream-100 px-2 py-0.5 rounded-full">
                    Not configured
                  </span>
                )}
              </div>
              <p className="freud-text-sm text-chocolate-500">
                Auto-push new leads from assessments and lead magnet sign-ups into your HubSpot CRM as contacts and deals
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Portal ID <span className="text-chocolate-400 font-normal">(optional, for reference)</span>
              </label>
              <input
                type="text"
                value={hubspotPortalId}
                onChange={(e) => setHubspotPortalId(e.target.value)}
                placeholder="e.g. 12345678"
                className="freud-input font-mono text-sm"
              />
              <p className="mt-1 text-xs text-chocolate-400">
                Found in your HubSpot URL: app.hubspot.com/contacts/<strong>portal-id</strong>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Pipeline ID
              </label>
              <input
                type="text"
                value={hubspotPipelineId}
                onChange={(e) => setHubspotPipelineId(e.target.value)}
                placeholder="e.g. default or abc123def456"
                className="freud-input font-mono text-sm"
              />
              <p className="mt-1 text-xs text-chocolate-400">
                HubSpot &rarr; CRM &rarr; Deals &rarr; select pipeline &rarr; Settings &rarr; copy the Pipeline ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Pipeline Stage ID <span className="text-chocolate-400 font-normal">(for new leads)</span>
              </label>
              <input
                type="text"
                value={hubspotPipelineStageId}
                onChange={(e) => setHubspotPipelineStageId(e.target.value)}
                placeholder="e.g. appointmentscheduled"
                className="freud-input font-mono text-sm"
              />
              <p className="mt-1 text-xs text-chocolate-400">
                The stage where incoming deals should land. Get the ID from HubSpot &rarr; Settings &rarr; Objects &rarr; Deals &rarr; Pipelines.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-700 mb-1.5">
                Private App Access Token <span className="text-chocolate-400 font-normal">(required)</span>
              </label>
              {hasHubspotToken && !replaceHubspotToken ? (
                <div className="flex items-center justify-between rounded-xl border border-sage-200 bg-sage-50 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-600" />
                    <span className="text-sm text-sage-700 font-medium">Token configured (encrypted)</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplaceHubspotToken(true)}
                    className="text-xs font-medium text-sage-700 hover:text-sage-900 underline"
                  >
                    Replace
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <input
                      type={showHubspotToken ? 'text' : 'password'}
                      value={hubspotAccessToken}
                      onChange={(e) => setHubspotAccessToken(e.target.value)}
                      placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="freud-input pr-20 font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowHubspotToken(!showHubspotToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-chocolate-400 hover:text-chocolate-600"
                    >
                      {showHubspotToken ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {hasHubspotToken && (
                    <button
                      type="button"
                      onClick={() => { setReplaceHubspotToken(false); setHubspotAccessToken(''); }}
                      className="text-xs text-chocolate-500 hover:text-chocolate-700 mt-1"
                    >
                      Cancel replace
                    </button>
                  )}
                </>
              )}
              <p className="mt-1 text-xs text-chocolate-400">
                HubSpot &rarr; Settings &rarr; Integrations &rarr; Private Apps &rarr; create app with <strong>crm.objects.contacts.write</strong> and <strong>crm.objects.deals.write</strong> scopes
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveHubspot}
                disabled={isSavingHubspot}
                className="freud-btn-primary px-6 py-2.5 disabled:opacity-60 text-sm"
              >
                {isSavingHubspot ? 'Saving...' : 'Save HubSpot Settings'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Enable Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cream-100 rounded-lg">
                <Brain className="w-5 h-5 text-chocolate-600" />
              </div>
              <div>
                <h2 className="freud-heading-base text-chocolate-900">Enable AI Coach</h2>
                <p className="freud-text-sm text-chocolate-500">
                  {setupCompleted
                    ? 'Toggle to make AI Coach available for your respondents'
                    : 'Complete the setup wizard first to enable'}
                </p>
              </div>
            </div>

            <button
              onClick={() => setupCompleted && setIsEnabled(!isEnabled)}
              disabled={!setupCompleted}
              className="group flex items-center gap-2"
            >
              <div
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  isEnabled ? 'bg-sage-500' : 'bg-gray-300'
                } ${!setupCompleted ? 'opacity-40' : ''}`}
              >
                <div
                  className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                    isEnabled ? 'translate-x-[30px]' : 'translate-x-0.5'
                  }`}
                />
              </div>
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-end"
        >
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="freud-btn-primary px-8 py-3 disabled:opacity-60"
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </motion.div>
      </main>
    </div>
  );
}
